import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faUsers, faUserCheck, faUserTimes, faCalendar, faShieldAlt } from '@fortawesome/free-solid-svg-icons';
import { Form, Button, InputGroup, Table, Spinner, Badge } from '@themesberg/react-bootstrap';
import { useHistory } from 'react-router-dom';
import { fetchUsers, setPage, setSearch, clearError } from '../redux/slices/usersSlice';
import { toast } from '../components/Toaster';

export default () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { users, loading, error, pagination, page, search, hasLoaded, lastFetchedPage, lastFetchedSearch } = useSelector(state => state.users);
  const [footerStyle, setFooterStyle] = useState({});

  useEffect(() => {
    const updateFooterBounds = () => {
      const main = document.querySelector('main.content');
      if (main) {
        const rect = main.getBoundingClientRect();
        setFooterStyle({
          position: 'fixed',
          left: `${rect.left}px`,
          width: `${rect.width}px`,
          bottom: 0,
          background: '#ffffff',
          borderTop: '1px solid #e5e7eb',
          padding: '0.75rem 1rem',
          zIndex: 1030,
        });
      }
    };
    updateFooterBounds();
    window.addEventListener('resize', updateFooterBounds);
    return () => window.removeEventListener('resize', updateFooterBounds);
  }, []);

  // Fetch users on mount if not already loaded (smart caching)
  useEffect(() => {
    if (!hasLoaded) {
      dispatch(fetchUsers({ page, search }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const shouldRefetch = hasLoaded && (page !== lastFetchedPage || search !== lastFetchedSearch);
    if (shouldRefetch) {
      dispatch(fetchUsers({ page, search }));
    }
  }, [page, search, hasLoaded, lastFetchedPage, lastFetchedSearch, dispatch]);

  // Show error toast if loading fails
  useEffect(() => {
    if (error && hasLoaded) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch, hasLoaded]);

  const handleRowClick = (userId) => {
    history.push(`/users/${userId}`);
  };

  const handlePageChange = (newPage) => {
    dispatch(setPage(newPage));
  };

  const handleSearchChange = (value) => {
    dispatch(setSearch(value));
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      "admin": "danger",
      "user": "primary",
      "moderator": "warning",
    };
    return colors[role?.toLowerCase()] || "secondary";
  };

  return (
    <>
      <style>{`
        .page-header {
          background: #ffffff;
          border-radius: 12px;
          padding: 2rem;
          margin-bottom: 1.5rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          border-left: 4px solid #4f46e5;
        }
        
        .page-header h2 {
          font-size: 2rem;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 0.5rem;
        }
        
        .page-header p {
          color: #64748b;
          font-size: 1rem;
          margin-bottom: 0;
        }
        
        .stats-badge {
          background: #f1f5f9;
          padding: 0.5rem 1rem;
          border-radius: 8px;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          font-weight: 600;
          color: #4f46e5;
          margin-top: 1rem;
        }
        
        .search-card {
          background: white;
          border-radius: 12px;
          padding: 1.5rem;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          margin-bottom: 1.5rem;
        }
        
        .search-input {
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          padding: 0.6rem 1rem;
          transition: all 0.3s ease;
        }
        
        .search-input:focus {
          border-color: #4f46e5;
          box-shadow: 0 0 0 3px rgba(79, 70, 229, 0.1);
        }
        
        .users-table {
          background: white;
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }
        
        .users-table table {
          margin-bottom: 0;
        }
        
        .users-table thead {
          background: #f8fafc;
        }
        
        .users-table th {
          font-weight: 700;
          color: #1e293b;
          padding: 1rem;
          border-bottom: 2px solid #e2e8f0;
          font-size: 0.875rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        
        .users-table tbody tr {
          cursor: pointer;
          transition: all 0.2s ease;
          border-bottom: 1px solid #f1f5f9;
        }
        
        .users-table tbody tr:hover {
          background: #f8fafc;
        }
        
        .users-table td {
          padding: 1rem;
          vertical-align: middle;
          color: #475569;
        }
        
        .user-name {
          font-weight: 600;
          color: #1e293b;
          font-size: 0.95rem;
        }
        
        .user-email {
          color: #64748b;
          font-size: 0.85rem;
        }
        
        .action-btn {
          border-radius: 8px;
          padding: 0.5rem 1rem;
          font-weight: 600;
          border: 1px solid #e2e8f0;
          background: white;
          color: #4f46e5;
          transition: all 0.3s ease;
          font-size: 0.85rem;
        }
        
        .action-btn:hover {
          background: #4f46e5;
          color: white;
          border-color: #4f46e5;
          transform: translateY(-1px);
        }
        
        .page-btn {
          border-radius: 8px;
          padding: 0.6rem 1.25rem;
          font-weight: 600;
          border: 1px solid #e2e8f0;
          background: white;
          color: #475569;
          transition: all 0.3s ease;
        }
        
        .page-btn:hover:not(:disabled) {
          background: #4f46e5;
          color: white;
          border-color: #4f46e5;
        }
        
        .page-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
        
        .empty-state {
          text-align: center;
          padding: 4rem 2rem;
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }
        
        .empty-state svg {
          font-size: 3rem;
          color: #cbd5e0;
          margin-bottom: 1rem;
        }
        
        .empty-state h3 {
          color: #475569;
          margin-bottom: 0.5rem;
        }
        
        .empty-state p {
          color: #94a3b8;
        }
        
        .loading-container {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 4rem;
          background: white;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }

        .status-indicator {
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.85rem;
          font-weight: 600;
        }

        .status-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
        }

        .status-active .status-dot {
          background: #10b981;
          box-shadow: 0 0 0 3px rgba(16, 185, 129, 0.2);
        }

        .status-inactive .status-dot {
          background: #ef4444;
          box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.2);
        }
        
        @media (max-width: 768px) {
          .users-table {
            overflow-x: auto;
          }
        }
      `}</style>

      <div className="page-header">
        <div className="d-flex justify-content-between align-items-start flex-wrap gap-3">
          <div>
            <h2>All Users</h2>
            <p>Manage and monitor user accounts</p>
            <div className="stats-badge">
              <FontAwesomeIcon icon={faUsers} />
              {pagination.totalUsers || 0} Total Users
            </div>
          </div>
        </div>
      </div>

      <div className="search-card">
        <h5 className="mb-3" style={{ fontWeight: 700, color: '#1e293b' }}>
          <FontAwesomeIcon icon={faSearch} className="me-2" />
          Search Users
        </h5>
        <InputGroup>
          <InputGroup.Text style={{ 
            background: 'white', 
            border: '1px solid #e2e8f0',
            borderRight: 'none',
            borderRadius: '8px 0 0 8px'
          }}>
            <FontAwesomeIcon icon={faSearch} style={{ color: '#4f46e5' }} />
          </InputGroup.Text>
          <Form.Control
            type="text"
            placeholder="Search by name or email..."
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="search-input"
            style={{ borderLeft: 'none', borderRadius: '0 8px 8px 0' }}
          />
        </InputGroup>
      </div>

      {/* Initial load - show nothing while fetching */}
      {loading && !hasLoaded ? (
        <div className="loading-container" style={{ minHeight: '300px' }}>
          {/* Silent loading on first mount */}
        </div>
      ) : loading && hasLoaded ? (
        <div className="loading-container">
          <Spinner animation="border" style={{ color: '#4f46e5', width: '3rem', height: '3rem' }} />
        </div>
      ) : users.length === 0 ? (
        <div className="empty-state">
          <FontAwesomeIcon icon={faUsers} />
          <h3>No Users Found</h3>
          <p>Try adjusting your search criteria</p>
        </div>
      ) : (
        <>
          <div className="users-table">
            <Table responsive hover>
              <thead>
                <tr>
                  <th>User</th>
                  <th style={{ width: '120px' }}>Role</th>
                  <th style={{ width: '100px' }}>Active</th>
                  <th style={{ width: '100px' }}>Verified</th>
                  <th style={{ width: '150px' }}>Created</th>
                  <th style={{ width: '120px' }} className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u._id} onClick={() => handleRowClick(u._id)}>
                    <td>
                      <div className="user-name">{u.name}</div>
                      <div className="user-email">{u.email || u.phone || '—'}</div>
                    </td>
                    <td>
                      <Badge 
                        bg={getRoleBadgeColor(u.role)} 
                        style={{ 
                          fontSize: '0.8rem', 
                          padding: '0.4rem 0.75rem',
                          textTransform: 'capitalize'
                        }}
                      >
                        <FontAwesomeIcon icon={faShieldAlt} className="me-1" />
                        {u.role}
                      </Badge>
                    </td>
                    <td>
                      <div className={`status-indicator ${u.isActive ? 'status-active' : 'status-inactive'}`}>
                        <span className="status-dot"></span>
                        <span style={{ color: u.isActive ? '#10b981' : '#ef4444' }}>
                          {u.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </td>
                    <td>
                      {u.isVerified ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#10b981' }}>
                          <FontAwesomeIcon icon={faUserCheck} />
                          <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>Verified</span>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#94a3b8' }}>
                          <FontAwesomeIcon icon={faUserTimes} />
                          <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>Unverified</span>
                        </div>
                      )}
                    </td>
                    <td>
                      <div style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <FontAwesomeIcon icon={faCalendar} style={{ color: '#94a3b8' }} />
                        {new Date(u.createdAt).toLocaleDateString('en-US', { 
                          month: 'short', 
                          day: 'numeric', 
                          year: 'numeric' 
                        })}
                      </div>
                    </td>
                    <td className="text-center">
                      <button
                        className="action-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          history.push(`/users/${u._id}`);
                        }}
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          {users.length > 0 && (
            <div style={footerStyle}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 600, color: '#475569' }}>
                  Page {page} of {pagination.totalPages} ({pagination.totalUsers} total users)
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                  <Button
                    className="page-btn"
                    disabled={page === 1}
                    onClick={() => handlePageChange(Math.max(1, page - 1))}
                  >
                    Previous
                  </Button>
                  {(() => {
                    const pages = []; 
                    const maxVisible = 5;
                    let start = Math.max(1, page - Math.floor(maxVisible / 2));
                    let end = Math.min(pagination.totalPages, start + maxVisible - 1);
                    if (end - start < maxVisible - 1) {
                      start = Math.max(1, end - maxVisible + 1);
                    }
                    for (let i = start; i <= end; i++) {
                      pages.push(
                        <Button
                          key={i}
                          className="page-btn"
                          style={i === page ? { background: '#4f46e5', color: 'white', borderColor: '#4f46e5' } : {}}
                          onClick={() => handlePageChange(i)}
                        >
                          {i}
                        </Button>
                      );
                    }
                    return pages;
                  })()}
                  <Button
                    className="page-btn"
                    disabled={page >= pagination.totalPages}
                    onClick={() => handlePageChange(page + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </div>
          )}
          {users.length > 0 && (
            <div style={{ height: '64px' }} />
          )}
        </>
      )}
    </>
  );
};